import React from 'react';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/16x16/hex.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/16x16/lightdash.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/16x16/looker.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/16x16/power-bi.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/16x16/sigma.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/16x16/tableau.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/16x16/thoughtspot.svg';
import themeBase from '@elementary/common-ui/style/theme';
import { FetchBiIntegrationApi } from 'shared/types';
import multipleDestinationsRenderer from '../../shared/multipleDestinationsRenderer';

export const platformIconMap: Record<string, React.ReactNode> = {
  Tableau: <TableauIcon />,
  Looker: <LookerIcon />,
  PowerBI: <PowerBiIcon />,
  Sigma: <SigmaIcon />,
  ThoughtSpot: <ThoughtSpotIcon />,
  Hex: <HexIcon />,
  Lightdash: <LightdashIcon />,
};

export type BiMultipleDestinationsRendererProps = {
  biIntegrations: FetchBiIntegrationApi[];
  onChange: (method: string, id: string) => void;
};

const GENERIC_BI_SYNC_ERROR_TOOLTIP =
  'Sync failed. Please check your connection settings, or contact support if the issue persists.';

const biMultipleDestinationsRenderer = ({
  biIntegrations,
  onChange,
}: BiMultipleDestinationsRendererProps): React.ReactNode =>
  multipleDestinationsRenderer({
    integrations: biIntegrations.map((biIntegration) => {
      // Temporary solution: Derive status from last_sync_status.
      // Note: Using last_sync_status isn't ideal - an error doesn't necessarily mean the integration
      // is disconnected. It might indicate an issue on our side (e.g., sync process failure).
      // However, as a temporary solution, we show "error" when last_sync_status is "error" because
      // most of the time it's due to a connection/auth issue, which is better than showing
      // a static "connected" status when there's actually a problem.
      // When status is "running", we don't show any status chip.
      const status =
        biIntegration.last_sync_status === 'error'
          ? 'error'
          : biIntegration.last_sync_status === 'running'
          ? undefined
          : 'connected';

      return {
        id: biIntegration.id,
        icon: platformIconMap[biIntegration.bi_type],
        name: biIntegration.name,
        status,
        statusTooltip:
          status === 'error'
            ? biIntegration.last_sync_error_message ||
              GENERIC_BI_SYNC_ERROR_TOOLTIP
            : null,
        last_synced_at: biIntegration.last_synced_at,
      };
    }),
    onChange,
    options: [
      { value: 'update', label: 'Edit connection' },
      {
        value: 'delete',
        label: 'Delete connection',
        color: themeBase.palette.common.red2,
      },
    ],
  });

export default biMultipleDestinationsRenderer;
